import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  ArrowLeft,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Leaf,
  Filter,
  Wind,
  Brain,
  Apple,
  Heart,
  Mail,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Section navigation links
const SECTION_NAV = [
  { name: "Home", path: "/benessere", icon: Leaf },
  { name: "Articoli", path: "/benessere/articoli", icon: BookOpen },
  { name: "Tecniche", path: "/benessere/tecniche", icon: Sparkles },
  { name: "Newsletter", path: "/benessere#newsletter", icon: Mail },
];

// Section Navigation Component
const SectionNavigation = () => (
  <nav className="bg-white/90 backdrop-blur-md border-b border-slate-100 py-3" data-testid="section-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <span className="text-xs text-slate-500 uppercase tracking-wide mr-2 whitespace-nowrap">Sezioni:</span>
        {SECTION_NAV.map((section) => {
          const IconComponent = section.icon;
          const isActive = section.path === "/benessere/articoli";
          return (
            <Link 
              key={section.path} 
              to={section.path}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                isActive 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-100 hover:bg-teal-100 text-slate-700 hover:text-teal-700'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              {section.name}
            </Link>
          );
        })}
      </div>
    </div>
  </nav>
);

const SECTOR_CONFIG = {
  estetica: { 
    gradient: "from-rose-500 to-pink-600",
    lightBg: "bg-rose-50",
    textColor: "text-rose-600",
    name: "Estetica Oncologica"
  },
  parrucchieri: { 
    gradient: "from-amber-500 to-orange-600",
    lightBg: "bg-amber-50",
    textColor: "text-amber-600",
    name: "Hair Care"
  },
  farmacie: { 
    gradient: "from-emerald-500 to-teal-600",
    lightBg: "bg-emerald-50",
    textColor: "text-emerald-600",
    name: "Farmacia"
  },
  retail: { 
    gradient: "from-violet-500 to-purple-600",
    lightBg: "bg-violet-50",
    textColor: "text-violet-600",
    name: "Retail"
  },
  centri_benessere: { 
    gradient: "from-sky-500 to-blue-600",
    lightBg: "bg-sky-50",
    textColor: "text-sky-600",
    name: "Wellness"
  },
  // Categorie Benessere
  meditazione: {
    gradient: "from-purple-500 to-indigo-600",
    lightBg: "bg-purple-50",
    textColor: "text-purple-600",
    name: "Meditazione"
  },
  respirazione: {
    gradient: "from-cyan-500 to-blue-600",
    lightBg: "bg-cyan-50",
    textColor: "text-cyan-600",
    name: "Respirazione"
  },
  yoga: {
    gradient: "from-teal-500 to-emerald-600",
    lightBg: "bg-teal-50",
    textColor: "text-teal-600",
    name: "Yoga"
  },
  stress: {
    gradient: "from-orange-500 to-red-500",
    lightBg: "bg-orange-50",
    textColor: "text-orange-600",
    name: "Gestione Stress"
  },
  alimentazione: {
    gradient: "from-green-500 to-lime-600",
    lightBg: "bg-green-50",
    textColor: "text-green-600",
    name: "Alimentazione"
  },
  benessere_generale: {
    gradient: "from-pink-500 to-rose-600",
    lightBg: "bg-pink-50",
    textColor: "text-pink-600",
    name: "Benessere Generale"
  },
  natura: {
    gradient: "from-emerald-500 to-green-600",
    lightBg: "bg-emerald-50",
    textColor: "text-emerald-600",
    name: "Natura e Benessere"
  },
  sonno: {
    gradient: "from-indigo-500 to-purple-600",
    lightBg: "bg-indigo-50",
    textColor: "text-indigo-600",
    name: "Sonno e Riposo"
  }
};

export default function ArticleListPage() {
  const { sectorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [articles, setArticles] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    total_pages: 0
  });
  
  const [selectedSector, setSelectedSector] = useState(sectorId || "all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadSectors();
  }, []);

  useEffect(() => {
    if (sectorId) {
      setSelectedSector(sectorId);
    }
  }, [sectorId]);

  useEffect(() => {
    loadArticles();
  }, [selectedSector, pagination.page]);

  const loadSectors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/sectors`);
      if (response.ok) {
        const data = await response.json();
        setSectors(data.sectors || []);
      }
    } catch (error) {
      console.error("Errore caricamento settori:", error);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/public/articles?limit=${pagination.limit}&page=${pagination.page}`;
      if (selectedSector && selectedSector !== "all") {
        url += `&sector=${selectedSector}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setArticles(data.articles || []);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          total_pages: data.total_pages
        }));
      }
    } catch (error) {
      console.error("Errore caricamento articoli:", error);
      toast.error("Errore nel caricamento degli articoli");
    } finally {
      setLoading(false);
    }
  };

  const handleSectorChange = (value) => {
    setSelectedSector(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentSector = sectorId ? SECTOR_CONFIG[sectorId] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="article-list-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
            </Link>
            
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                Area Riservata
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      <SectionNavigation />

      {/* Hero Section for Category */}
      {currentSector && (
        <section className={`relative py-16 bg-gradient-to-r ${currentSector.gradient}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/benessere" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Home
            </Link>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="category-title">
              {currentSector.name}
            </h1>
            <p className="text-white/80 max-w-2xl">
              Articoli e approfondimenti sul settore {currentSector.name.toLowerCase()}
            </p>
          </div>
        </section>
      )}

      {/* Newsletter AI Promo Section */}
      {!sectorId && (
        <section className="bg-gradient-to-r from-rose-500 to-pink-600 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-2/5">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/06b77105-3c32-4871-969f-a30c12ceda3f/images/63b784f3875d4409abce0ee3d5a3df5c46b84aa68a6d1db06cfb554d682870da.png"
                  alt="Newsletter AI"
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
                />
              </div>
              <div className="lg:w-3/5 text-center lg:text-left">
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
                  Ricevi Articoli Personalizzati nella Tua Email
                </h2>
                <p className="text-white/90 mb-4 leading-relaxed">
                  Iscriviti alla nostra newsletter e ricevi contenuti scientifici selezionati per te. 
                  Puoi anche <strong>fare domande all'AI</strong> e ricevere articoli scientifici 
                  personalizzati direttamente nella tua casella di posta!
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                  <Link to="/benessere#newsletter">
                    <Button className="bg-white text-rose-600 hover:bg-white/90 rounded-full px-6">
                      Iscriviti Gratis
                    </Button>
                  </Link>
                  <Link to="/benessere#ai-question">
                    <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                      Fai una Domanda all'AI
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Title (if not category) */}
        {!sectorId && (
          <div className="mb-8">
            <Link to="/benessere" className="inline-flex items-center text-slate-500 hover:text-slate-700 mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Home
            </Link>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900" data-testid="page-title">
              Tutti gli Articoli
            </h1>
            <p className="text-slate-600 mt-2">
              {pagination.total} articoli pubblicati
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8" data-testid="filters-section">
          <div className="flex-1 max-w-xs">
            <Select value={selectedSector} onValueChange={handleSectorChange}>
              <SelectTrigger className="rounded-xl">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filtra per settore" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i Settori</SelectItem>
                {sectors.map(sector => (
                  <SelectItem key={sector.id} value={sector.id}>
                    {sector.icon} {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20" data-testid="no-articles">
            <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun articolo trovato</h3>
            <p className="text-slate-500 mb-6">
              {selectedSector !== "all" 
                ? "Non ci sono ancora articoli pubblicati in questa categoria."
                : "Non ci sono ancora articoli pubblicati."}
            </p>
            {selectedSector !== "all" && (
              <Button variant="outline" onClick={() => setSelectedSector("all")} className="rounded-full">
                Mostra tutti gli articoli
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
              {articles.map((article) => {
                // Usa categoria_display se disponibile, altrimenti sector
                const sectorKey = article.sector || "estetica";
                const config = SECTOR_CONFIG[sectorKey] || SECTOR_CONFIG.estetica;
                // Se c'è categoria_display, usala come nome visualizzato
                const displayName = article.categoria_display || config.name;
                
                return (
                  <Link key={article.article_id} to={`/benessere/articolo/${article.article_id}`}>
                    <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden h-full" data-testid={`article-card-${article.article_id}`}>
                      {/* Header Image */}
                      {article.header_image && (
                        <div className="h-40 overflow-hidden bg-slate-100">
                          <img 
                            src={article.header_image} 
                            alt={article.title}
                            className="w-full h-full object-cover object-[center_20%] group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      {!article.header_image && (
                        <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />
                      )}
                      <CardContent className="pt-6">
                        <Badge className={`${config.lightBg} ${config.textColor} mb-4`}>
                          {displayName}
                        </Badge>
                        <h3 className="font-semibold text-lg text-slate-800 mb-3 group-hover:text-rose-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-slate-500 text-sm line-clamp-3 mb-4">
                          {article.meta_description || article.seo?.meta_description || "Leggi l'articolo completo..."}
                        </p>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span className="flex items-center">
                            <BookOpen className="w-3.5 h-3.5 mr-1" />
                            {article.word_count} parole
                          </span>
                          <span>
                            {new Date(article.created_at).toLocaleDateString("it-IT", {
                              day: "numeric",
                              month: "short"
                            })}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pagination.page === pageNum ? "default" : "ghost"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`rounded-full w-10 h-10 ${
                          pagination.page === pageNum 
                            ? "bg-rose-600 hover:bg-rose-700" 
                            : ""
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                  className="rounded-full"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} ODR - Oncocosmetics Derma Ritual. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}
